// Helpers de la facette PRODUCTEUR du module Paniers (proto v7.3, palier 1).
// Timeline 5 etapes pilotee par Tour.statut — une seule etape « a faire » a la fois.

export const ORANGE = 'rgb(var(--c-primary))'
export const TEAL = 'rgb(var(--c-teal))'
export const VERT = 'rgb(var(--c-green))'

export const eur = (n) => `${Number(n || 0).toFixed(2).replace('.', ',')} €`

/**
 * DEUX formateurs, parce qu'il y a deux natures de date — et les melanger
 * decalait l'affichage d'un jour en Martinique (UTC-4). Voir la regle en tete
 * de `api.js` et `docs/DESIGN-GUIDE.md` §11.10.
 */

/** JOUR calendaire (`@db.Date` : `dateCollecte`, `dateLivraison`) → lecture UTC. */
export const fmtJour = (iso) => {
  if (!iso) return ''
  const d = new Date(iso)
  if (isNaN(d.getTime())) return ''
  const jours = ['dim.', 'lun.', 'mar.', 'mer.', 'jeu.', 'ven.', 'sam.']
  const mois = ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin',
    'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.']
  return `${jours[d.getUTCDay()]} ${d.getUTCDate()} ${mois[d.getUTCMonth()]}`
}

/**
 * `fmtDate` reste exporte et vaut `fmtJour` : l'ECRASANTE majorite des dates
 * Paniers sont des jours calendaires (`dateLivraison`, `dateCollecte`,
 * `dateClotureCommandes`, `dateEmission`). Le defaut est donc le bon cas — les
 * rares instants passent explicitement par `fmtInstant`.
 */
export const fmtDate = fmtJour

/** INSTANT (`DateTime` vrai : `repartitionAt`, `fenetreFinAt`) → heure locale. */
export const fmtInstant = (iso) => {
  if (!iso) return ''
  try {
    return new Date(iso).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' })
  } catch {
    return ''
  }
}

// Les 5 etapes du parcours (proto v7.3 §2) — cle = ecran associe.
export const ETAPES = [
  { k: 'DISPONIBILITES', titre: 'Je propose', e: '📋', scr: 'dispos' },
  { k: 'COMMANDES', titre: 'Ça se vend', e: '📈', scr: 'demande' },
  { k: 'PREPARATION', titre: 'Je récolte et je prépare', e: '🧺', scr: 'preparation' },
  { k: 'LIVRAISON', titre: "C'est parti", e: '🚚', scr: 'livraison' },
  { k: 'TERMINE', titre: 'Mon argent arrive', e: '💶', scr: 'argent' },
]

const RANG = { DISPONIBILITES: 0, COMMANDES: 1, PREPARATION: 2, LIVRAISON: 3, TERMINE: 4 }

/** Index de l'etape en cours (0-4) ; 5 = tout est fini (argent reparti). */
export const etapeCourante = (t) => {
  const rang = RANG[t.statut] ?? 0
  if (t.statut !== 'TERMINE') return rang
  // TERMINE : l'etape « Mon argent » reste en cours tant qu'un net est retenu.
  // Sans aucune vente en ligne (argent null), rien n'est attendu : soldee.
  return (t.argent?.netPrevisionnel ?? 0) > 0 ? 4 : 5
}

/** Etat d'une etape i pour la timeline : fait / encours / avenir. */
export const etapeEtat = (t, i) => {
  const cur = etapeCourante(t)
  if (i < cur) return 'fait'
  if (i === cur && cur <= 4) return 'encours'
  return cur >= 5 ? 'fait' : 'avenir'
}

/** La prochaine tache de la tournee (texte du hero + carte liste). */
export const tacheDe = (t) => {
  switch (t.statut) {
    case 'DISPONIBILITES':
      return t.nbProduits === 0
        ? { txt: 'Choisissez vos produits et saisissez vos disponibilités', urgent: true }
        : { txt: `${t.nbProduits} produit${t.nbProduits > 1 ? 's' : ''} proposé${t.nbProduits > 1 ? 's' : ''} — ajustable jusqu'à la clôture`, urgent: false }
    case 'COMMANDES':
      return { txt: `Ça se vend — ${Number(t.totalReserve || 0)} réservés sur ${Number(t.totalDispo || 0)} proposés`, urgent: false }
    case 'PREPARATION':
      return { txt: `Préparez votre liste — collecte ${fmtDate(t.dateCollecte)}`, urgent: true }
    case 'LIVRAISON':
      return { txt: 'Manrina livre — rien à faire', urgent: false }
    default: {
      if ((t.argent?.netPrevisionnel ?? 0) > 0) {
        const fin = t.argent?.prochaineFenetreFin
        return { txt: `Net en séquestre${fin ? ` — fenêtre jusqu'au ${fmtInstant(fin)}` : ''}`, urgent: false }
      }
      if (!t.argent) return { txt: 'Tournée terminée', urgent: false }
      return {
        txt: t.versementMode === 'WALLET' ? 'Crédité sur votre wallet' : 'Versé sur votre compte',
        urgent: false,
      }
    }
  }
}

/** Tri de « Mes tournees » (decision Enzo 12/07) : a venir de la plus proche a la
 * plus lointaine, puis les livrees dont l'argent bouge encore. Les tournees
 * entierement versees/creditees partent a l'Historique. */
export const trierTournees = (tours) =>
  [...tours]
    .filter((t) => etapeCourante(t) < 5)
    .sort((a, b) => {
      const fa = a.statut === 'TERMINE'
      const fb = b.statut === 'TERMINE'
      if (fa !== fb) return fa ? 1 : -1
      const da = new Date(a.dateLivraison).getTime()
      const db = new Date(b.dateLivraison).getTime()
      return fa ? db - da : da - db
    })

/** Tournees terminees ET soldees → Historique (les plus recentes d'abord). */
export const tourneesHistorique = (tours) =>
  [...tours]
    .filter((t) => etapeCourante(t) >= 5)
    .sort((a, b) => new Date(b.dateLivraison) - new Date(a.dateLivraison))
